import React from 'react';

interface SummaryFiltersProps {
  summarySearchTerm: string;
  setSummarySearchTerm: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  rankFilter: string;
  setRankFilter: (value: string) => void;
  activityFilter: string;
  setActivityFilter: (value: string) => void;
  uniqueRanks: string[];
  onClearFilters: () => void;
  filteredCount: number;
  totalCount: number;
}

const SummaryFilters: React.FC<SummaryFiltersProps> = ({
  summarySearchTerm, setSummarySearchTerm,
  statusFilter, setStatusFilter,
  rankFilter, setRankFilter,
  activityFilter, setActivityFilter,
  uniqueRanks, onClearFilters,
  filteredCount, totalCount,
}) => {
  const hasActiveFilters = summarySearchTerm || statusFilter || rankFilter || activityFilter;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        <button
          onClick={onClearFilters}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Clear All Filters
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Employees
          </label>
          <input
            type="text"
            value={summarySearchTerm}
            onChange={(e) => setSummarySearchTerm(e.target.value)}
            placeholder="Search by name or rank..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Current Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="on-vessel">On Vessel</option>
            <option value="on-vacation">On Vacation</option>
            <option value="off-duty">Off Duty</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rank
          </label>
          <select
            value={rankFilter}
            onChange={(e) => setRankFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Ranks</option>
            {uniqueRanks.map(rank => (
              <option key={rank} value={rank}>
                {rank}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Activity
          </label>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Any Time</option>
            <option value="recent">Last 7 days</option>
            <option value="moderate">8-30 days ago</option>
            <option value="old">Over 30 days ago</option>
          </select>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center text-sm text-blue-800">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
            </svg>
            <span>
              Showing {filteredCount} of {totalCount} employees
              {summarySearchTerm && ` • Search: "${summarySearchTerm}"`}
              {statusFilter && ` • Status: ${statusFilter.replace('-', ' ')}`}
              {rankFilter && ` • Rank: ${rankFilter}`}
              {activityFilter && ` • Activity: ${activityFilter === 'recent' ? 'Recent' : activityFilter === 'moderate' ? 'Moderate' : 'Old'}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryFilters;
